import { DEFAULT_PAGE_SIZE } from '@/constants'
import type {
  UpdateFiltersParams,
  UpdateFiltersResult,
} from '@/services/filters/filtersTypes'
import { useUserInfoStore } from '@/stores/user/userInfoStore'
import {
  AvailableFiltersType,
  FilterSearchResponseInterface,
  SearchNomenclatureRequestBody,
} from '@/types'
import api from '@/utils/api/api'
import { isNomenclatureEditor } from '@/utils/auth/userRoles'
import { hasUniformEligibleSlottedFilter } from '@/utils/search/characteristics/slottedCharacteristics'
import {
  buildCharacteristicFilterBody,
  buildStoresRequestPart,
} from '@/utils/search/request/buildSearchRequestBody'

export default function useUpdateFilters() {
  const currentClient = useUserInfoStore((state) => state.currentClient)
  const isEditor = useUserInfoStore((state) =>
    isNomenclatureEditor(state.tokenRoles)
  )

  const updateFilters = async (
    params: UpdateFiltersParams
  ): Promise<UpdateFiltersResult> => {
    const {
      query,
      categories,
      brands,
      characteristicFilters,
      withLeftovers,
      stores,
      parents,
      withDiscontinued,
      matchUniformSlottedCharacteristics = false,
    } = params

    let clientId: string | null = null
    if (currentClient?.id) {
      clientId = currentClient.id
    }

    const shouldMatchUniformSlotted =
      matchUniformSlottedCharacteristics &&
      hasUniformEligibleSlottedFilter(characteristicFilters)

    // Записка §10: `/nomenclatures/filters` тоже перешёл на POST с JSON-телом
    // `SearchNomenclatureRequest` — GET больше не биндит `filter[...]`.
    const requestBody: SearchNomenclatureRequestBody = {
      query: query,
      ...(clientId && { client: clientId }),
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
      exactMatch: false,
      withLeftovers: isEditor ? false : withLeftovers,
      withDiscontinued: isEditor ? withDiscontinued : false,
      matchUniformSlottedCharacteristics: shouldMatchUniformSlotted,
      categoryIds: categories.map((category) => category.id),
      brandIds: brands.map((brand) => brand.id),
      parentIds: parents.map((p) => p.id),
      ...buildStoresRequestPart(stores),
      ...buildCharacteristicFilterBody(characteristicFilters),
    }

    try {
      const response = await api.post<FilterSearchResponseInterface>(
        `/nomenclatures/filters`,
        requestBody
      )

      const filtersData = response.data.filters
      const availableFilters = filtersData?.availableFilters
      const totalElements = filtersData?.totalElements

      return {
        availableFilters: availableFilters ?? ({} as AvailableFiltersType),
        totalElements: totalElements || 0,
      }
    } catch (error) {
      // Возврат null означает, что вызывающий код оставит на экране прежние
      // значения фасета. Без подробностей в консоли это выглядит как «фильтр
      // не обновляется», хотя на деле бэкенд вернул ошибку — печатаем причину.
      const serverMessage = (
        error as { response?: { data?: { message?: string } } } | undefined
      )?.response?.data?.message
      console.error(
        'Ошибка при обновлении фильтров — значения фасета останутся прежними.' +
          (serverMessage ? ` Ответ бэкенда: ${serverMessage}` : ''),
        { params: requestBody, error }
      )
      return null
    }
  }

  return updateFilters
}
